import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Loader2, Send, FlaskConical, RefreshCw, Eye, CalendarClock, XCircle, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDate, timeAgo } from "@/lib/constants";

const AUDIENCE_LABELS = { contacts: "Contacts", leads: "Leads", dealers: "Dealers", universe: "Universe", custom: "Custom list", adhoc: "Quick send" };
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const STAT_CARDS = [
  { key: "sent", label: "Sent", color: "text-slate-900" },
  { key: "delivered", label: "Delivered", color: "text-emerald-700" },
  { key: "opened", label: "Opened", color: "text-blue-700" },
  { key: "clicked", label: "Clicked", color: "text-indigo-700" },
  { key: "bounced", label: "Bounced", color: "text-amber-700" },
  { key: "unsubscribed", label: "Unsubscribed", color: "text-red-700" },
];

function recipientState(r) {
  if (r.unsubscribed_at) return ["Unsubscribed", "bg-red-100 text-red-700"];
  if (r.bounced_at) return ["Bounced", "bg-amber-100 text-amber-800"];
  if (r.clicked_at) return ["Clicked", "bg-indigo-100 text-indigo-800"];
  if (r.opened_at) return ["Opened", "bg-blue-100 text-blue-800"];
  if (r.delivered_at) return ["Delivered", "bg-emerald-100 text-emerald-800"];
  if (r.status === "failed") return ["Failed", "bg-red-100 text-red-700"];
  if (r.status === "sent") return ["Sent", "bg-slate-100 text-slate-600"];
  return ["Queued", "bg-slate-100 text-slate-500"];
}

export default function CampaignDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const [testEmail, setTestEmail] = useState(user?.email || "");
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [schedule, setSchedule] = useState("");
  const [showPreview, setShowPreview] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["campaign", id],
    queryFn: async () => (await api.get(`/marketing/campaigns/${id}`)).data,
    refetchInterval: (q) => (["sending", "scheduled"].includes(q.state.data?.campaign?.status) ? 5000 : false),
  });

  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); if (ok) toast.success(ok); qc.invalidateQueries({ queryKey: ["campaign", id] }); qc.invalidateQueries({ queryKey: ["campaigns"] }); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  if (isLoading || !data) return <div className="py-20 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  const { campaign: c, stats, recipients } = data;
  const isDraft = ["draft", "failed"].includes(c.status);

  return (
    <div>
      <button onClick={() => navigate("/marketing/campaigns")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-4" data-testid="campaign-back-btn">
        <ArrowLeft className="h-4 w-4" /> Campaigns
      </button>
      <PageHeader title={c.name} subtitle={`Subject: ${c.subject}`}>
        <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${
          { draft: "bg-slate-100 text-slate-600", scheduled: "bg-amber-100 text-amber-800", sending: "bg-blue-100 text-blue-800", sent: "bg-emerald-100 text-emerald-800", failed: "bg-red-100 text-red-700" }[c.status]}`} data-testid="campaign-status-badge">{c.status}</span>
        <Button size="sm" variant="outline" className="h-8 gap-1" onClick={() => setShowPreview(true)} data-testid="campaign-preview-btn"><Eye className="h-4 w-4" /> Email Preview</Button>
        {isDraft && (
          <Button size="sm" variant="outline" className="h-8 gap-1 text-red-600 border-red-200 hover:bg-red-50" onClick={() => act(() => api.delete(`/marketing/campaigns/${id}`).then(() => navigate("/marketing/campaigns")), "Campaign deleted")} data-testid="campaign-delete-btn"><Trash2 className="h-4 w-4" /></Button>
        )}
      </PageHeader>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <Card className="p-5" data-testid="campaign-summary-card">
          <h3 className="font-heading font-semibold text-sm mb-3">Summary</h3>
          <div className="space-y-1.5 text-sm">
            <p><span className="text-slate-500">Template:</span> {c.template_name}</p>
            <p><span className="text-slate-500">Audience:</span> <span>{AUDIENCE_LABELS[c.audience_source] || c.audience_source}</span>
              {c.audience_filters?.list_name ? ` · ${c.audience_filters.list_name}` : ""}
              {c.audience_filters?.zone ? ` · ${c.audience_filters.zone}` : ""}
              {(c.audience_filters?.categories || []).length ? ` · Class ${(c.audience_filters.categories).join("/")}` : ""}</p>
            <p><span className="text-slate-500">Recipients:</span> <span className="font-mono font-bold" data-testid="campaign-recipient-count">{c.recipient_count}</span></p>
            <p><span className="text-slate-500">Sender:</span> {c.sender_name} &lt;{c.sender_email}&gt;</p>
            <p><span className="text-slate-500">Created by:</span> {c.created_by_name} · {formatDate(c.created_at)}</p>
            {c.sent_at && <p><span className="text-slate-500">Sent:</span> {formatDate(c.sent_at)}</p>}
            {c.error && <p className="text-red-600 text-xs">{c.error}</p>}
          </div>
        </Card>

        {isDraft && (
          <>
            <Card className="p-5 border-blue-200" data-testid="test-send-card">
              <div className="flex items-center gap-2 mb-2"><FlaskConical className="h-4 w-4 text-blue-600" /><h3 className="font-heading font-semibold text-sm">1 · Test send (required)</h3></div>
              <p className="text-xs text-slate-500 mb-3">Send the email to yourself first — with sample merge data and a working unsubscribe link.</p>
              <div className="flex gap-2">
                <Input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="you@united-group.in" className="h-9" data-testid="test-email-input" />
                <Button size="sm" className="h-9 bg-blue-600 hover:bg-blue-700" disabled={busy} onClick={() => act(() => api.post(`/marketing/campaigns/${id}/test`, { email: testEmail }), `Test sent to ${testEmail} — check your inbox`)} data-testid="test-send-btn">Send Test</Button>
              </div>
              {c.test_sent_at && <p className="text-xs text-emerald-700 mt-2" data-testid="test-sent-note">✓ Test sent to {c.test_sent_to} · {timeAgo(c.test_sent_at)}</p>}
            </Card>

            <Card className={`p-5 ${c.test_sent_at ? "border-emerald-200" : "opacity-70"}`} data-testid="launch-card">
              <div className="flex items-center gap-2 mb-2"><Send className="h-4 w-4 text-emerald-600" /><h3 className="font-heading font-semibold text-sm">2 · Launch</h3></div>
              {!c.test_sent_at && <p className="text-xs text-amber-700 mb-2">Send a test first — launching is locked until then.</p>}
              <Button className="w-full bg-emerald-600 hover:bg-emerald-700 gap-1.5 mb-2" disabled={!c.test_sent_at || busy} onClick={() => { setSchedule(""); setConfirmOpen(true); }} data-testid="send-now-btn"><Send className="h-4 w-4" /> Send Now</Button>
              <div className="flex gap-2">
                <Input type="datetime-local" value={schedule} onChange={(e) => setSchedule(e.target.value)} className="h-9 text-sm" data-testid="schedule-input" />
                <Button variant="outline" size="sm" className="h-9 gap-1" disabled={!c.test_sent_at || !schedule || busy} onClick={() => setConfirmOpen(true)} data-testid="schedule-btn"><CalendarClock className="h-4 w-4" /> Schedule</Button>
              </div>
            </Card>
          </>
        )}

        {c.status === "scheduled" && (
          <Card className="p-5 border-amber-200" data-testid="scheduled-card">
            <div className="flex items-center gap-2 mb-2"><CalendarClock className="h-4 w-4 text-amber-600" /><h3 className="font-heading font-semibold text-sm">Scheduled</h3></div>
            <p className="text-sm mb-3">Sends at <span className="font-semibold">{new Date(c.scheduled_at).toLocaleString("en-IN")}</span></p>
            <Button variant="outline" size="sm" className="gap-1 text-red-600 border-red-200 hover:bg-red-50" disabled={busy} onClick={() => act(() => api.post(`/marketing/campaigns/${id}/cancel`), "Schedule cancelled — back to draft")} data-testid="cancel-schedule-btn"><XCircle className="h-4 w-4" /> Cancel schedule</Button>
          </Card>
        )}

        {["sending", "sent"].includes(c.status) && (
          <Card className="p-5 lg:col-span-2" data-testid="stats-card">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading font-semibold text-sm">Results</h3>
              <Button size="sm" variant="outline" className="h-8 gap-1 text-xs" disabled={busy} onClick={() => act(() => api.post(`/marketing/campaigns/${id}/sync-stats`), "Stats refreshed from Brevo")} data-testid="sync-stats-btn"><RefreshCw className="h-3.5 w-3.5" /> Refresh from Brevo</Button>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
              {STAT_CARDS.map((s) => (
                <div key={s.key} className="text-center p-2 rounded-lg bg-slate-50" data-testid={`stat-${s.key}`}>
                  <p className={`font-mono text-xl font-bold ${s.color}`}>{stats[s.key]}</p>
                  <p className="text-[11px] text-slate-500">{s.label}</p>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>

      {recipients.length > 0 && (
        <Card className="overflow-hidden" data-testid="recipients-table">
          <div className="overflow-x-auto max-h-[480px] overflow-y-auto">
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                {["Email", "Name", "Company", "Source", "Status"].map((h) => <TableHead key={h} className="text-xs uppercase tracking-wider text-slate-500">{h}</TableHead>)}
              </TableRow></TableHeader>
              <TableBody>
                {recipients.map((r) => {
                  const [label, cls] = recipientState(r);
                  return (
                    <TableRow key={r.id}>
                      <TableCell className="font-mono text-xs">{r.email}</TableCell>
                      <TableCell className="text-sm">{r.name || "—"}</TableCell>
                      <TableCell className="text-sm text-slate-600">{r.company || "—"}</TableCell>
                      <TableCell className="text-xs capitalize text-slate-500">{r.entity_type}</TableCell>
                      <TableCell><span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold ${cls}`}>{label}</span></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="sm:max-w-md" data-testid="send-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{schedule ? "Schedule this campaign?" : "Send this campaign now?"}</DialogTitle>
            <DialogDescription>Final check before anything goes out.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5 text-sm bg-slate-50 rounded-lg p-3">
            <p><span className="text-slate-500">Template:</span> {c.template_name}</p>
            <p><span className="text-slate-500">Audience:</span> <span>{AUDIENCE_LABELS[c.audience_source] || c.audience_source}</span></p>
            <p><span className="text-slate-500">Recipients:</span> <span className="font-mono font-bold">{c.recipient_count}</span></p>
            <p><span className="text-slate-500">Sender:</span> {c.sender_email}</p>
            {schedule && <p><span className="text-slate-500">Sends at:</span> {new Date(schedule).toLocaleString("en-IN")}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" disabled={busy} data-testid="confirm-send-btn"
              onClick={() => { setConfirmOpen(false); act(() => api.post(`/marketing/campaigns/${id}/send`, schedule ? { scheduled_at: new Date(schedule).toISOString() } : {}), schedule ? "Campaign scheduled" : "Sending — recipients are being processed"); }}>
              {schedule ? "Confirm Schedule" : `Send to ${c.recipient_count} recipients`}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-3xl h-[85vh] flex flex-col">
          <DialogHeader><DialogTitle className="font-heading">Email snapshot</DialogTitle></DialogHeader>
          <div className="flex-1 min-h-0 bg-slate-100 rounded-lg p-3 overflow-auto flex justify-center">
            <iframe title="Campaign email" srcDoc={c.html} className="bg-white rounded shadow border-0 h-full w-full max-w-[700px]" data-testid="campaign-html-iframe" />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
